<template>
    <div>
        <div v-show="showBar" ref="bar" :style="[barStyle, {bottom: mobile ? '56px' : 0}]">
            <div class="zoezi-row-limit-width d-flex align-center" :class="mobile ? 'flex-column pa-3' : 'pa-4'">
                <div class="flex-grow-1" :class="mobile ? 'mb-2' : 'mr-4'">
                    <div>{{ $translate('COOKIE_CONSENT_BAR_TEXT') }}</div>
                    <div class="mt-1">
                        {{ $translate('You can also choose for yourself under') }}
                        <span class="z-clickable primary--text text-decoration-underline font-weight-medium" role="button" tabindex="0" @click="openDialog" @keydown.enter.prevent="openDialog" @keydown.space.prevent="openDialog">{{ $translate('Cookie settings') }}</span>.
                    </div>
                    <div v-if="error" class="error--text" v-text="$translate('Something went wrong')" />
                </div>
                <div class="d-flex flex-shrink-0" :class="mobile ? 'fill-width' : 'cookie-consent-bar-buttons'">
                    <zoezi-button radius="25px" large thin :text="$translate('Only necessary')" :loading="saving == 'necessary'" :disabled="!!saving" @click="save(false, false, 'necessary')" class="ma-1 cookie-consent-bar-button" />
                    <zoezi-button radius="25px" large thin :text="$translate('Accept all')" :loading="saving == 'all'" :disabled="!!saving" @click="save(true, true, 'all')" class="ma-1 cookie-consent-bar-button" />
                </div>
            </div>
        </div>

        <!-- A first choice must be made with a button; once one exists, or saving failed, the dialog can be left. -->
        <v-dialog v-model="showDialog" :persistent="!canClose" scrollable :max-width="mobile ? undefined : 700" :fullscreen="mobile">
            <v-card>
                <v-card-title class="text-h5" v-text="$translate('Cookie settings')" />
                <v-card-text class="text--primary">
                    <p v-text="$translate('COOKIE_CONSENT_OVERVIEW_TEXT')" />

                    <div v-for="category in categories" :key="category.key" class="mt-6">
                        <div class="d-flex align-center">
                            <div class="text-h6 flex-grow-1" v-text="$translate(category.title)" />
                            <v-switch
                                v-if="category.locked"
                                :input-value="true"
                                disabled
                                hide-details
                                class="mt-0 pt-0"
                                :label="$translate('Always active')"
                            />
                            <v-switch
                                v-else
                                :input-value="choice[category.key]"
                                @change="choice[category.key] = !!$event"
                                hide-details
                                class="mt-0 pt-0"
                                :label="$translate(choice[category.key] ? 'Activated' : 'Inactivated')"
                            />
                        </div>
                        <p class="mt-2 mb-0" v-text="$translate(category.text)" />
                    </div>

                    <p class="mt-6" v-text="$translate('COOKIE_CONSENT_POLICY_TEXT')" />
                    <p v-if="policyLink && policyLink.href">{{ $translate('More information about') }} <a target="_blank" :href="policyLink.href">{{ $translate('PRIVACY_POLICY') }}</a></p>

                    <div v-if="error" class="error--text" v-text="$translate('Something went wrong')" />
                </v-card-text>
                <v-divider />
                <v-card-actions class="d-flex justify-center" :class="{'flex-column': mobile}">
                    <zoezi-button radius="25px" large thin :text="$translate('Save choice')" :loading="saving == 'choice'" :disabled="!!saving" @click="save(choice.statistics, choice.marketing, 'choice')" class="ma-1" :class="{'fill-width': mobile}" />
                    <zoezi-button radius="25px" large thin :text="$translate('Only necessary')" :loading="saving == 'necessary'" :disabled="!!saving" @click="save(false, false, 'necessary')" class="ma-1" :class="{'fill-width': mobile}" />
                    <zoezi-button radius="25px" large thin :text="$translate('Accept all')" :loading="saving == 'all'" :disabled="!!saving" @click="save(true, true, 'all')" class="ma-1" :class="{'fill-width': mobile}" />
                    <v-btn v-if="canClose" text :disabled="!!saving" @click="showDialog = false" class="ma-1">{{ $translate('Close') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import {getConsent, saveConsent, getCookie} from '../util.js'
import {isHomepageFixHorizontalScrollEnabled} from '../featureFlags'

export default {
    name: 'zoezi-cookie-consent',

    props: {
        policyLink: Object
    },

    data: () => ({
        consent: null,
        showDialog: false,
        choice: {statistics: false, marketing: false},
        saving: null,
        error: false,
        categories: [
            {key: 'necessary', title: 'Necessary cookies', text: 'COOKIE_CONSENT_NECESSARY_TEXT', locked: true},
            {key: 'statistics', title: 'Statistics', text: 'COOKIE_STATISTICS_TEXT'},
            {key: 'marketing', title: 'Marketing', text: 'COOKIE_MARKETING_TEXT'}
        ]
    }),

    computed: {
        mobile() {
            return this.$vuetify.breakpoint.mobile;
        },
        showBar() {
            return !this.consent && !this.showDialog;
        },
        canClose() {
            return !!this.consent || this.error;
        },
        barStyle() {
            const style = {position: 'fixed', backgroundColor: 'white', zIndex: 10, borderTop: '1px solid #c9c9c9'};
            if (isHomepageFixHorizontalScrollEnabled(this)) {
                style.left = 0;
                style.right = 0;
            } else {
                style.width = '100vw';
            }
            return style;
        }
    },

    watch: {
        showBar() {
            this.$nextTick(this.reportBarHeight);
        }
    },

    methods: {
        openDialog() {
            this.choice = {
                statistics: !!(this.consent && this.consent.statistics),
                marketing: !!(this.consent && this.consent.marketing)
            };
            this.error = false;
            this.showDialog = true;
        },
        async save(statistics, marketing, button) {
            this.saving = button;
            this.error = false;
            const ok = await saveConsent(statistics, marketing);
            this.saving = null;
            if (!ok) {
                this.error = true;
                return;
            }
            this.consent = getConsent();
            this.showDialog = false;
        },
        // The iOS app reports its tracking prompt answer every time the web view starts.
        // It decides only until the visitor has chosen; after that a no may lower the choice, a yes never raises it.
        onAppConsent(accept) {
            const current = getConsent();
            // A choice made against older texts still counts: the banner asks again, the app's yes doesn't answer for the visitor.
            const hasChosen = !!current || !!getCookie('zoeziConsent');
            if (!hasChosen) {
                this.save(!!accept, !!accept, accept ? 'all' : 'necessary');
            } else if (!accept && (!current || current.statistics || current.marketing)) {
                this.save(false, false, 'necessary');
            }
        },
        // Root lifts its fixed buttons above the bar by this much.
        reportBarHeight() {
            this.$root.$emit('cookieBarHeight', this.$refs.bar ? this.$refs.bar.offsetHeight : 0);
        }
    },

    mounted() {
        this.consent = getConsent();
        this.$root.$on('openCookieSettings', this.openDialog);
        this.$root.$on('setThirdpartyCookieStatus', this.onAppConsent);
        // The app may have answered before this component existed.
        if (window.__zoeziAppTrackingConsent !== undefined) {
            this.onAppConsent(window.__zoeziAppTrackingConsent);
        }

        // Fires when the bar is shown, hidden or wraps to another height.
        if (window.ResizeObserver) {
            this.barObserver = new ResizeObserver(() => this.reportBarHeight());
            this.barObserver.observe(this.$refs.bar);
        }
        this.$nextTick(this.reportBarHeight);
    },

    beforeDestroy() {
        this.$root.$off('openCookieSettings', this.openDialog);
        this.$root.$off('setThirdpartyCookieStatus', this.onAppConsent);
        if (this.barObserver) {
            this.barObserver.disconnect();
        }
        this.$root.$emit('cookieBarHeight', 0);
    }
}
</script>

<style scoped>
/* Equal width for both choices, so neither is visually favoured. */
.cookie-consent-bar-button {
    flex: 1 1 0;
}
.cookie-consent-bar-buttons {
    min-width: 400px;
}
</style>
